import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, PhoneCall, MessageCircle, Eye, TrendingUp, Clock, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { buildWaLink } from "../lib/usePlatformWa";

/**
 * Staff-side "Trial Follow-ups" widget.
 * Assigned Sales staff sees only the shops they onboarded (via
 * verifications.assigned_staff_id). Platform admin sees all.
 */
export default function StaffTrialFollowups() {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get("/staff/trial-followups");
      setState({ loading: false, error: "", data: r.data });
    } catch (err) {
      setState({ loading: false, error: err?.response?.data?.detail || "Failed to load", data: null });
    }
  };
  useEffect(() => { load(); }, []);

  if (state.loading) return <div className="grid place-items-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (state.error)   return <div className="rounded-md border border-red-900 bg-red-950/40 text-red-200 p-4 text-sm">{state.error}</div>;

  const { buckets = {}, shops = [] } = state.data || {};

  const kpis = [
    { label: "Expiring Today",    value: buckets.expiring_today || 0,    icon: Clock,          tone: "amber",   testid: "staff-trial-today" },
    { label: "Expiring Tomorrow", value: buckets.expiring_tomorrow || 0, icon: AlertTriangle,  tone: "amber",   testid: "staff-trial-tomorrow" },
    { label: "Expired",           value: buckets.expired || 0,           icon: AlertTriangle,  tone: "rose",    testid: "staff-trial-expired" },
    { label: "Converted",         value: buckets.converted || 0,         icon: CheckCircle2,   tone: "emerald", testid: "staff-trial-converted" },
    { label: "Pending Follow-up", value: buckets.pending_followup || 0,  icon: TrendingUp,     tone: "sky",     testid: "staff-trial-pending" },
  ];

  return (
    <div className="space-y-5" data-testid="staff-trial-followups-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Trial Follow-ups</h1>
          <p className="text-slate-600 text-sm mt-1">Free-trial shops assigned to you. Reach out, convert, unlock commission.</p>
        </div>
        <Button variant="outline" onClick={load} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="staff-trial-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className={`rounded-md border p-3 ${
            k.tone === "amber" ? "border-amber-200 bg-amber-50" :
            k.tone === "rose" ? "border-rose-200 bg-rose-50" :
            k.tone === "emerald" ? "border-emerald-200 bg-emerald-50" :
            "border-sky-200 bg-sky-50"
          }`} data-testid={k.testid}>
            <div className="flex items-center gap-2"><k.icon className={`w-4 h-4 ${
              k.tone === "amber" ? "text-amber-700" :
              k.tone === "rose" ? "text-rose-700" :
              k.tone === "emerald" ? "text-emerald-700" : "text-sky-700"
            }`} /><div className="text-[11px] uppercase tracking-widest font-bold text-slate-600">{k.label}</div></div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 text-slate-900 font-semibold">Shops ({shops.length})</div>
        {shops.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No trial shops assigned to you.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {shops.map((s) => (
              <div key={s.shop_id} className="p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`staff-trial-row-${s.shop_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-900 font-semibold">{s.shop_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.owner_name || "—"} · {s.owner_mobile || s.owner_email || "no contact"} {s.city ? `· ${s.city}` : ""}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Registered {(s.registered_at || "").slice(0, 10)} · Trial ends {(s.trial_ends || "").slice(0, 10)} · <b className={
                      s.converted ? "text-emerald-700" :
                      s.days_delta < 0 ? "text-rose-700" :
                      s.days_delta === 0 ? "text-amber-700" : "text-slate-700"
                    }>
                      {s.converted ? "Converted" :
                       s.days_delta < 0 ? `${-s.days_delta} day(s) expired` :
                       s.days_delta === 0 ? "Expires today" :
                       `${s.days_delta} day(s) remaining`}
                    </b>
                    {s.referred_by ? ` · Referred by ${s.referred_by}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {s.owner_mobile ? (
                    <>
                      <a href={`tel:${s.owner_mobile}`} className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1" data-testid={`staff-trial-call-${s.shop_id}`}>
                        <PhoneCall className="w-3.5 h-3.5" /> Call
                      </a>
                      <a href={buildWaLink(s.owner_mobile, `Assalam-o-Alaikum ${s.owner_name || ""}, HisaabPro trial expiry ke bare mein baat karni thi.`)} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1" data-testid={`staff-trial-wa-${s.shop_id}`}>
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    </>
                  ) : null}
                  <a href={`/staff/shops/${encodeURIComponent(s.shop_id)}`} className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1" data-testid={`staff-trial-view-${s.shop_id}`}>
                    <Eye className="w-3.5 h-3.5" /> View
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
